import { computed } from 'vue'
import { usePageContext } from "vike-vue/usePageContext";
import { capitalize, getAddressFromDeal, getAddressPart, cleanAddressPart } from '../utils/seo'; // Import centralized helpers
import type { LinkedPage, Deal, LocaleCode, Page } from '@repo/types'; // Import types
import type { PageDataBase } from '../hooks'
import type { PageContext } from 'vike/types';

export function useAddress() {
  const pageContext = usePageContext() as PageContext<PageDataBase>;

  // Return default empty values if pageContext.data or pageContext.data.page is missing
  if (!pageContext.data?.page) {
    return {
      breadcrumbs: computed(() => []),
      page: computed(() => undefined),
      deal: computed(() => undefined),
      address: computed(() => '')
    };
  }

  // Ensure page and property are reactive in a computed way
  const page = computed<LinkedPage | undefined>(() => pageContext.data.page);
  const property = computed(() => pageContext.data.property);
  const deal = computed<Deal | undefined>(() => property.value?.deals?.[0]);
  const locale = computed<LocaleCode>(() => (pageContext.locale as LocaleCode) ?? 'en');

  const addressComponents = computed(() => {
    if (!deal.value || !page.value || !deal.value.building.addressComponents) {
      return { prefecture: '', city: '', town: '', chome: undefined, banchi: undefined };
    }
    const levels = page.value.parents?.concat(page.value) || [page.value];
    const { chome, banchi } = deal.value.building.addressComponents;
    return {
      prefecture: getAddressPart(levels.find((p: Page) => p?.level == 'prefecture')),
      city: getAddressPart(levels.find((p: Page) => p?.level == 'city')),
      town: getAddressPart(levels.find((p: Page) => p?.level == 'town')),
      chome: chome && parseInt(chome.normalize()),
      banchi
    };
  });

  const address = computed(() => {
    if (!deal.value) return '';

    const localizedAddress = getAddressFromDeal(deal.value, locale.value)
    if (localizedAddress) return localizedAddress

    // Use locally derived addressComponents
    const parts = [
      [
        addressComponents.value.chome,
        addressComponents.value.banchi,
      ].filter(Boolean).join('-'),
      addressComponents.value.town,
      addressComponents.value.city,
      addressComponents.value.prefecture,
    ].filter(Boolean).map(cleanAddressPart);

    return parts.map(capitalize).join(', ');
  });

  const pageName = computed<string>(() => {
    if (deal.value && locale.value === 'ja') {
      return deal.value.name ?? deal.value.building.name ?? deal.value.building.address
    }
    return address.value || getLocaleName(page.value, locale.value)
  })

  const getLocaleName = (p: Page | LinkedPage | undefined, loc: LocaleCode) =>
    p && loc === 'ja' ? p.name : getAddressPart(p)

  const breadcrumbs = computed(() => {
    if (!page.value) return [];
    const crumbs = page.value.parents.map(p => ({
      ...p,
      name: getLocaleName(p, locale.value)
    }));

    // We add the current page (or property name) as the last crumb
    // Using unknown cast here because the last crumb doesn't need to be a full Page object for the UI
    return [...crumbs, { name: pageName.value }];
  });

  return {
    page,
    deal,
    breadcrumbs,
    address,
    getAddressPart: computed(() => (p: Page | LinkedPage | undefined) => getLocaleName(p, locale.value))
  }
}
